import { Trophy, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import type { ComparisonResult } from "../../../types";
import { METHODOLOGY_DISCLOSURE } from "../../../methodology";
import type { RunGroupResult } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/ui/markdown";
import { ScoreBar } from "@/components/ui/score-bar";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useComparisonsStore } from "@/stores/comparisons";
import { SentimentResults } from "./SentimentResults";
import { cn } from "@/lib/utils";

type LoadingPresentation = "spinner" | "none";

export function ComparisonResults({ loadingPresentation = "spinner" }: { loadingPresentation?: LoadingPresentation }) {
  const { activeComparison, activeRunGroup, batchMessage, clearBatchMessage } =
    useComparisonsStore();

  if (batchMessage) {
    return (
      <div className="animate-fade-up rounded-lg border border-brand-100 bg-brand-50 px-5 py-4 flex items-center justify-between">
        <p className="text-[13px] text-brand-800">{batchMessage}</p>
        <button
          onClick={clearBatchMessage}
          className="text-xs text-brand-600 hover:text-brand-500 font-medium cursor-pointer"
        >
          Dismiss
        </button>
      </div>
    );
  }

  // Run group polling
  if (activeRunGroup) {
    if (activeRunGroup.status === "polling") {
      if (loadingPresentation === "none") return null;

      return (
        <Spinner
          message={`Completed ${activeRunGroup.completedCount} of ${activeRunGroup.expectedCount} runs...`}
          size="lg"
        />
      );
    }

    if (activeRunGroup.status === "completed" && activeRunGroup.results) {
      return (
        <RunGroupResultsView
          results={activeRunGroup.results}
          runCount={activeRunGroup.expectedCount}
        />
      );
    }
  }

  // Single comparison polling
  if (activeComparison) {
    if (activeComparison.status === "polling") {
      if (loadingPresentation === "none") return null;

      return <Spinner message="Running evaluation..." size="lg" />;
    }

    if (activeComparison.status === "failed") {
      return (
        <div className="animate-fade-up rounded-lg border border-red-200 bg-red-50 px-5 py-4">
          <p className="text-[13px] text-red-700">
            Evaluation failed. Please try again.
          </p>
        </div>
      );
    }

    if (activeComparison.status === "completed" && activeComparison.result) {
      if (activeComparison.result.prompt_type === "sentiment" || activeComparison.result.query_mode === "blind_query") {
        return <SentimentResults result={activeComparison.result} />;
      }
      return <SingleResultView result={activeComparison.result} />;
    }
  }

  return null;
}

function SingleResultView({ result }: { result: ComparisonResult }) {
  const dimensions =
    result.results.length > 0 ? Object.keys(result.results[0]!.scores) : [];

  // Find the max score per dimension for winner highlighting
  const maxByDim: Record<string, number> = {};
  for (const dim of dimensions) {
    maxByDim[dim] = Math.max(...result.results.map((x) => x.scores[dim] ?? 0));
  }

  return (
    <div className="animate-fade-up space-y-6">
      <MethodologyNote />

      {/* Summary Header */}
      {(result.winner || result.summary) && (
        <Card>
          <CardContent className="py-5 px-6">
            <div className="flex items-start gap-4">
              {result.winner && (
                <div className="shrink-0 w-10 h-10 rounded-lg bg-accent-50 border border-accent-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-accent-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {result.winner && (
                  <p className="text-sm font-semibold text-brand-950">
                    {result.winner}{" "}
                    <span className="font-normal text-slate-500">
                      leads this evaluation
                    </span>
                  </p>
                )}
                {result.summary && (
                  <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">
                    {result.summary}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Brand Score Cards */}
      <div className="stagger-children space-y-4">
        {result.results.map((r) => {
          const isWinner = r.brand_name === result.winner;
          return (
            <BrandScoreCard
              key={r.brand_id}
              brandName={r.brand_name}
              isWinner={isWinner}
              scores={r.scores}
              dimensions={dimensions}
              maxByDim={maxByDim}
              reasoning={r.reasoning}
            />
          );
        })}
      </div>
    </div>
  );
}

function BrandScoreCard({
  brandName,
  isWinner,
  scores,
  dimensions,
  maxByDim,
  reasoning,
}: {
  brandName: string;
  isWinner: boolean;
  scores: Record<string, number | null>;
  dimensions: string[];
  maxByDim: Record<string, number>;
  reasoning: string | null;
}) {
  const [showReasoning, setShowReasoning] = useState(false);
  const avgScore = dimensions.length > 0
    ? Math.round(dimensions.reduce((sum, d) => sum + (scores[d] ?? 0), 0) / dimensions.length)
    : 0;

  return (
    <Card className={cn(isWinner && "border-accent-500/30 bg-accent-50/20")}>
      <CardContent className="py-5 px-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h4 className="text-[15px] font-semibold text-brand-950">
              {brandName}
            </h4>
            {isWinner && <Badge variant="winner">Winner</Badge>}
          </div>
          <span
            className={cn(
              "text-lg font-bold tabular-nums",
              isWinner ? "text-accent-700" : "text-brand-950"
            )}
          >
            {avgScore}
            <span className="text-xs font-normal text-slate-400 ml-0.5">
              avg
            </span>
          </span>
        </div>

        <div className="space-y-2.5">
          {dimensions.map((dim) => (
            <ScoreBar
              key={dim}
              label={dim}
              score={scores[dim] ?? null}
              isWinner={
                isWinner && scores[dim] === maxByDim[dim]
              }
            />
          ))}
        </div>

        {reasoning && (
          <div className="mt-4">
            <button
              onClick={() => setShowReasoning(!showReasoning)}
              className="flex items-center gap-1 text-[12px] font-medium text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            >
              {showReasoning ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
              AI Reasoning
            </button>
            {showReasoning && (
              <div className="mt-2 rounded-md bg-slate-50 p-3 animate-fade-up">
                <Markdown content={reasoning} className="text-[13px] text-slate-600" />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RunGroupResultsView({
  results,
  runCount,
}: {
  results: RunGroupResult[];
  runCount: number;
}) {
  if (results.length === 0) return null;

  const dimensions = Object.keys(results[0]!.avg_scores);

  // Find the brand with most wins
  const maxWins = Math.max(...results.map((r) => r.wins));

  return (
    <div className="animate-fade-up space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Averaged Results</CardTitle>
          <CardDescription>
            Aggregated across {runCount} runs
          </CardDescription>
        </CardHeader>
      </Card>

      <MethodologyNote />

      <div className="stagger-children space-y-4">
        {results.map((r) => {
          const isLeader = r.wins === maxWins && r.wins > 0;
          const avgScore = dimensions.length > 0
            ? Math.round(
                dimensions.reduce(
                  (sum, d) => sum + (r.avg_scores[d] ?? 0),
                  0
                ) / dimensions.length
              )
            : 0;

          return (
            <Card
              key={r.brand_id}
              className={cn(isLeader && "border-accent-500/30 bg-accent-50/20")}
            >
              <CardContent className="py-5 px-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h4 className="text-[15px] font-semibold text-brand-950">
                      {r.brand_name}
                    </h4>
                    <Badge variant="muted">
                      {r.wins}/{runCount} wins
                    </Badge>
                  </div>
                  <span
                    className={cn(
                      "text-lg font-bold tabular-nums",
                      isLeader ? "text-accent-700" : "text-brand-950"
                    )}
                  >
                    {avgScore}
                    <span className="text-xs font-normal text-slate-400 ml-0.5">
                      avg
                    </span>
                  </span>
                </div>

                <div className="space-y-2.5">
                  {dimensions.map((dim) => (
                    <div key={dim} className="flex items-center gap-3">
                      <span className="text-[13px] text-slate-600 min-w-[100px] truncate">
                        {dim}
                      </span>
                      <div className="flex-1 bg-slate-100 rounded-full h-[5px] overflow-hidden">
                        <div
                          className={cn(
                            "h-[5px] rounded-full animate-score-fill",
                            isLeader ? "bg-accent-500" : "bg-brand-600"
                          )}
                          style={{
                            width: `${r.avg_scores[dim] ?? 0}%`,
                          }}
                        />
                      </div>
                      <span className="text-[13px] font-semibold text-brand-950 tabular-nums w-8 text-right">
                        {Math.round(r.avg_scores[dim] ?? 0)}
                      </span>
                      <span className="text-[11px] text-slate-400 tabular-nums w-[52px] text-right">
                        [{r.min_scores[dim]}&ndash;{r.max_scores[dim]}]
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function MethodologyNote() {
  return (
    <p className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-[12px] leading-relaxed text-slate-500">
      {METHODOLOGY_DISCLOSURE}
    </p>
  );
}
